import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { Plus, Trash2, RefreshCw, Zap, Copy, Search, Eye, KeyRound } from 'lucide-react';

const EMPTY_TOKEN = {
  chain: 'polygon', symbol: '', contract_address: '', decimals: 18,
  min_deposit: 1, credit_currency: '', enabled: true,
};

export const AdminBlockchainWalletsPage = () => {
  const [status, setStatus] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [search, setSearch] = useState('');
  const [tokenForm, setTokenForm] = useState(null);
  const [revealDialog, setRevealDialog] = useState(null); // { user, password, key }
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [s, w, t] = await Promise.all([
        adminAPI.getWalletEngineStatus(),
        adminAPI.listCustodialWallets(),
        adminAPI.listWatchedTokens(),
      ]);
      setStatus(s.data);
      setWallets(w.data);
      setTokens(t.data);
    } catch (e) { toast.error('Failed to load'); }
  };

  const toggleChain = async (key, value) => {
    setBusy(true);
    try {
      const chain_enabled = { ...status.chain_enabled, [key]: value };
      await adminAPI.updateWalletEngineConfig({ chain_enabled });
      toast.success('Updated');
      load();
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const bulkGenerate = async () => {
    if (!window.confirm('Generate custodial wallets for all users who don’t have one?')) return;
    setBusy(true);
    try {
      const res = await adminAPI.bulkGenerateCustodialWallets();
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const rescan = async (chain) => {
    setBusy(true);
    try {
      const res = await adminAPI.rescanWalletEngine({ chain, depth_blocks: 5000 });
      toast.success(res.data.message);
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const saveToken = async () => {
    try {
      const payload = {
        ...tokenForm,
        contract_address: (tokenForm.contract_address || '').trim() || null,
        credit_currency: (tokenForm.credit_currency || tokenForm.symbol).toUpperCase(),
        symbol: tokenForm.symbol.toUpperCase(),
      };
      if (tokenForm.id) {
        await adminAPI.updateWatchedToken(tokenForm.id, payload);
        toast.success('Updated');
      } else {
        await adminAPI.createWatchedToken(payload);
        toast.success('Added');
      }
      setTokenForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const removeToken = async (t) => {
    if (!window.confirm(`Remove ${t.chain} · ${t.symbol}?`)) return;
    try {
      await adminAPI.deleteWatchedToken(t.id);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const doReveal = async () => {
    try {
      const res = await adminAPI.revealPrivateKey(revealDialog.user.user_id, revealDialog.password);
      setRevealDialog({ ...revealDialog, key: res.data.private_key });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const filteredWallets = wallets.filter((w) =>
    !search || (w.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (w.username || '').toLowerCase().includes(search.toLowerCase()) ||
    w.address.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-wallet-engine-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Blockchain Wallets</h1>
            <p className="mt-2 text-muted-foreground">Custodial wallet engine — auto-generates addresses, watches for deposits and credits users.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => rescan(null)} disabled={busy} data-testid="rescan-all-btn"><RefreshCw className="h-4 w-4 mr-2" /> Rescan all</Button>
            <Button onClick={bulkGenerate} disabled={busy} data-testid="bulk-generate-btn"><Zap className="h-4 w-4 mr-2" /> Generate missing</Button>
          </div>
        </div>

        {status && (
          <div className="grid gap-3 md:grid-cols-3">
            {Object.entries(status.chains).map(([k, c]) => (
              <Card key={k}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{c.name}</p>
                    <Switch
                      checked={!!status.chain_enabled[k]}
                      onCheckedChange={(v) => toggleChain(k, v)}
                      disabled={busy}
                      data-testid={`toggle-chain-${k}`}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>Chain ID: {c.chain_id}</div>
                    <div>Latest block: <code>{status.block_heights[k]}</code></div>
                    <div>Last scanned: <code>{status.last_scanned_block?.[k] ? parseInt(status.last_scanned_block[k], 16) : '—'}</code></div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => rescan(k)} disabled={busy} data-testid={`rescan-${k}-btn`}><RefreshCw className="h-3 w-3 mr-1" /> Rescan</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="wallets">
          <TabsList>
            <TabsTrigger value="wallets" data-testid="tab-wallets">Custodial wallets ({wallets.length})</TabsTrigger>
            <TabsTrigger value="tokens" data-testid="tab-tokens">Watched tokens ({tokens.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="wallets" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>User custodial wallets</CardTitle>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-9 w-72" placeholder="Search email / address…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wallet-search" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredWallets.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No custodial wallets yet. Click &quot;Generate missing&quot; above.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredWallets.slice(0, 100).map((w) => (
                        <TableRow key={w.user_id} data-testid={`wallet-row-${w.user_id}`}>
                          <TableCell>
                            <div className="text-sm">{w.email || w.user_id.substring(0, 12)}</div>
                            {w.username && <div className="text-xs text-muted-foreground">@{w.username}</div>}
                          </TableCell>
                          <TableCell><code className="text-xs">{w.address}</code></TableCell>
                          <TableCell className="text-xs">{formatDate(w.created_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard?.writeText(w.address); toast.success('Copied'); }} title="Copy address"><Copy className="h-4 w-4" /></Button>
                            {status && Object.entries(status.chains).filter(([k]) => status.chain_enabled?.[k]).map(([k, c]) => (
                              <a key={k} href={`${c.block_explorer_addr}${w.address}`} target="_blank" rel="noopener noreferrer" title={`View on ${c.name}`}>
                                <Button size="icon" variant="ghost"><Eye className="h-4 w-4" /></Button>
                              </a>
                            ))}
                            <Button size="icon" variant="ghost" onClick={() => setRevealDialog({ user: w, password: '', key: null })} title="Reveal private key" className="text-amber-500" data-testid={`reveal-key-${w.user_id}`}>
                              <KeyRound className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tokens" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Watched tokens</CardTitle>
                  <Button onClick={() => setTokenForm({ ...EMPTY_TOKEN })} data-testid="new-token-btn"><Plus className="h-4 w-4 mr-2" /> Add token</Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Chain</TableHead>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Contract</TableHead>
                      <TableHead>Min deposit</TableHead>
                      <TableHead>Credit as</TableHead>
                      <TableHead>Enabled</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tokens.map((t) => (
                      <TableRow key={t.id} data-testid={`token-row-${t.id}`}>
                        <TableCell><Badge variant="outline">{t.chain}</Badge></TableCell>
                        <TableCell className="font-medium">{t.symbol}</TableCell>
                        <TableCell><code className="text-xs">{t.contract_address || '— native —'}</code></TableCell>
                        <TableCell>{t.min_deposit}</TableCell>
                        <TableCell>{t.credit_currency}</TableCell>
                        <TableCell>
                          <Switch
                            checked={!!t.enabled}
                            onCheckedChange={async (v) => {
                              try { await adminAPI.updateWatchedToken(t.id, { enabled: v }); load(); }
                              catch (e) { toast.error('Failed'); }
                            }}
                            data-testid={`toggle-token-${t.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="icon" variant="ghost" onClick={() => setTokenForm({ ...t })}>✏️</Button>
                          <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeToken(t)}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Token dialog */}
      <Dialog open={!!tokenForm} onOpenChange={(v) => !v && setTokenForm(null)}>
        <DialogContent data-testid="token-dialog">
          <DialogHeader><DialogTitle>{tokenForm?.id ? 'Edit token' : 'Add watched token'}</DialogTitle></DialogHeader>
          {tokenForm && (
            <div className="space-y-3">
              <div>
                <Label>Chain</Label>
                <Select value={tokenForm.chain} onValueChange={(v) => setTokenForm({ ...tokenForm, chain: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {status && Object.entries(status.chains).map(([k, c]) => <SelectItem key={k} value={k}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Symbol</Label><Input value={tokenForm.symbol} onChange={(e) => setTokenForm({ ...tokenForm, symbol: e.target.value.toUpperCase() })} placeholder="e.g. USDC" data-testid="token-symbol-input" /></div>
                <div><Label>Credit as (internal currency)</Label><Input value={tokenForm.credit_currency} onChange={(e) => setTokenForm({ ...tokenForm, credit_currency: e.target.value.toUpperCase() })} placeholder="Defaults to Symbol" /></div>
              </div>
              <div><Label>Contract address (leave blank for native)</Label><Input value={tokenForm.contract_address || ''} onChange={(e) => setTokenForm({ ...tokenForm, contract_address: e.target.value })} className="font-mono text-xs" placeholder="0x…" data-testid="token-contract-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Decimals</Label><Input type="number" value={tokenForm.decimals} onChange={(e) => setTokenForm({ ...tokenForm, decimals: parseInt(e.target.value) || 18 })} /></div>
                <div><Label>Min deposit</Label><Input type="number" step="0.000001" value={tokenForm.min_deposit} onChange={(e) => setTokenForm({ ...tokenForm, min_deposit: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={tokenForm.enabled} onCheckedChange={(v) => setTokenForm({ ...tokenForm, enabled: v })} /><Label>Enabled</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenForm(null)}>Cancel</Button>
            <Button onClick={saveToken} data-testid="save-token-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal private key */}
      <Dialog open={!!revealDialog} onOpenChange={(v) => !v && setRevealDialog(null)}>
        <DialogContent data-testid="reveal-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-500">⚠ Reveal private key</DialogTitle>
            <DialogDescription>
              Only reveal in an emergency (wallet recovery, lost funds). This action is audit-logged forever.
            </DialogDescription>
          </DialogHeader>
          {revealDialog && (
            <div className="space-y-3">
              <p className="text-sm">User: <strong>{revealDialog.user.email}</strong></p>
              <p className="text-xs font-mono break-all">{revealDialog.user.address}</p>
              {!revealDialog.key ? (
                <div>
                  <Label>Confirm your admin password</Label>
                  <Input type="password" value={revealDialog.password} onChange={(e) => setRevealDialog({ ...revealDialog, password: e.target.value })} data-testid="reveal-password-input" />
                </div>
              ) : (
                <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-muted-foreground mb-1">Private key:</p>
                  <code className="text-xs font-mono break-all">{revealDialog.key}</code>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevealDialog(null)}>Close</Button>
            {revealDialog && !revealDialog.key && (
              <Button className="bg-amber-500" onClick={doReveal} data-testid="confirm-reveal-btn">Reveal</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBlockchainWalletsPage;
